import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { chevronLeftSmall, chevronRightSmall, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import CarouselLayoutEnum from '../../enums/CarouselLayoutEnum.js';

/**
 * Carousel Controls component.
 *
 * @event prev Emits when navigating to the previous item
 * @event next Emits when navigating to the next item
 */

export default class CarouselControls extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Appearance and function 'primary' or 'tertiary'.
	@property({ type: String })
	public buttonType: Extract<ButtonVariant, 'primary' | 'tertiary'> = 'primary';

	// Layout
	@property({ type: String })
	public layout: CarouselLayoutEnum = CarouselLayoutEnum.default;

	// Show left arrow
	@property({ type: Boolean })
	public showLeftArrow = false;

	// Show right arrow
	@property({ type: Boolean })
	public showRightArrow = false;

	// Label for the carousel to set as an aria-label for accessibility reasons
	@property({ type: String })
	public ariaLabelLeftArrow: string | null = null;

	// Label for the carousel to set as an aria-label for accessibility reasons
	@property({ type: String })
	public ariaLabelRightArrow: string | null = null;

	public override async connectedCallback(): Promise<void> {
		registerIcon(chevronLeftSmall);
		registerIcon(chevronRightSmall);

		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);

		super.connectedCallback();
	}

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const boxShadow = this.theme.elevation.size.default + ' ' + this.theme.elevation.color.default;
		const borderRadius = this.theme.border.radius.radius64 / 16 + 'rem';

		return html`
			<style>
				:host {
					display: flex;
					align-items: center;
				}
				.filler {
					flex-grow: 1;
					opacity: 0;
					pointer-events: none;
				}
				.arrow {
					display: flex;
					transition:
						visibility 0.3s,
						opacity 0.3s;
					opacity: 0;
					box-shadow: ${this.layout === CarouselLayoutEnum.overflow ? boxShadow : 'none'};
					border-radius: ${this.layout === CarouselLayoutEnum.overflow ? borderRadius : 'none'};
				}
				.arrow.left {
					opacity: ${this.showLeftArrow ? 1 : 0};
				}
				.arrow.right {
					opacity: ${this.showRightArrow ? 1 : 0};
				}
				.arrow skapa-icon-button {
					pointer-events: auto;
				}
			</style>
			${this.getPrevButton()}
			<div class="filler"></div>
			${this.getNextButton()}
		`;
	}

	/**
	 * @returns Template.
	 */
	private getPrevButton(): RenderResult {
		if (!this.showLeftArrow) {
			return null;
		}

		return html`
			<div class="arrow left">
				<skapa-icon-button
					variant="${this.buttonType}"
					size="${this.layout === CarouselLayoutEnum.overflow ? 'xsmall' : 'small'}"
					?inverse="${this.layout === CarouselLayoutEnum.overflow}"
					tabIndex="-1"
					@click="${() => {
						this.dispatchEvent(new CustomEvent('prev', { bubbles: true }));
					}}"
				>
					<skapa-icon slot="icon" flip-rtl icon="chevron-left-small"></skapa-icon>
					${this.ariaLabelLeftArrow || ''}
				</skapa-icon-button>
			</div>
		`;
	}

	/**
	 * @returns Template.
	 */
	private getNextButton(): RenderResult {
		if (!this.showRightArrow) {
			return null;
		}

		return html`
			<div class="arrow right">
				<skapa-icon-button
					variant="${this.buttonType}"
					size="${this.layout === CarouselLayoutEnum.overflow ? 'xsmall' : 'small'}"
					?inverse="${this.layout === CarouselLayoutEnum.overflow}"
					tabIndex="-1"
					@click="${() => {
						this.dispatchEvent(new CustomEvent('next', { bubbles: true }));
					}}"
				>
					<skapa-icon slot="icon" flip-rtl icon="chevron-right-small"></skapa-icon>
					${this.ariaLabelRightArrow || ''}
				</skapa-icon-button>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-icon': SkapaIcon;
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'kompis-carousel-controls': CarouselControls;
	}
}
